import useSWR, { type SWRConfiguration } from 'swr';

export type PolyPnlInterval = '1d' | '1w' | '1m' | 'all';
export type PolyPnlFidelity = '1h' | '3h' | '18h' | '1d';

export type TimeRange = '1D' | '1W' | '1M' | '1Y' | 'YTD' | 'ALL';

export const timeRanges: TimeRange[] = ['1D', '1W', '1M', '1Y', 'YTD', 'ALL'];

export const TIME_RANGE_INTERVAL_MAP: Record<TimeRange, PolyPnlInterval> = {
  '1D': '1d',
  '1W': '1w',
  '1M': '1m',
  '1Y': 'all',
  YTD: 'all',
  ALL: 'all',
};

export const TIME_RANGE_FIDELITY_MAP: Record<TimeRange, PolyPnlFidelity> = {
  '1D': '1h',
  '1W': '3h',
  '1M': '18h',
  '1Y': '1d',
  YTD: '1d',
  ALL: '1d',
};

export interface PolyPnlPoint {
  t: number;
  p: number;
}

export interface PolyPnlChartPoint {
  timestamp: number;
  value: number;
}

export type PolyPnlResponse = PolyPnlPoint[];

export interface FetchPolyPnlParams {
  wallets: string[];
  interval?: PolyPnlInterval;
  fidelity?: PolyPnlFidelity;
}

export interface UsePolyPnlOptions extends FetchPolyPnlParams {
  enabled?: boolean;
  swr?: SWRConfiguration<PolyPnlResponse, Error>;
}

const POLY_PNL_ENDPOINT = 'https://user-pnl-api.polymarket.com/user-pnl';
const DEFAULT_INTERVAL: PolyPnlInterval = '1d';
const DEFAULT_FIDELITY: PolyPnlFidelity = '1h';

export async function fetchPolyPnl({
  wallets,
  interval = DEFAULT_INTERVAL,
  fidelity = DEFAULT_FIDELITY,
}: FetchPolyPnlParams) {
  const normalizedWallets = normalizeWallets(wallets);
  if (normalizedWallets.length === 0) return [];

  const walletResponses = await Promise.all(
    normalizedWallets.map(async (wallet) => {
      const searchParams = new URLSearchParams({
        user_address: wallet,
        interval,
        fidelity,
      });

      const response = await fetch(`${POLY_PNL_ENDPOINT}?${searchParams.toString()}`, {
        headers: {
          accept: 'application/json, text/plain, */*',
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Polymarket PnL: ${response.status}`);
      }

      return (await response.json()) as PolyPnlResponse;
    }),
  );

  return mergePolyPnlResponses(walletResponses);
}

export function usePolyPnl(options: UsePolyPnlOptions) {
  const wallets = normalizeWallets(options.wallets);
  const interval = options.interval ?? DEFAULT_INTERVAL;
  const fidelity = options.fidelity ?? DEFAULT_FIDELITY;
  const enabled = options.enabled ?? true;

  const swrKey =
    enabled && wallets.length > 0 ? ['poly-pnl', wallets.join(','), interval, fidelity] : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<PolyPnlResponse, Error>(
    swrKey,
    () => fetchPolyPnl({ wallets, interval, fidelity }),
    {
      revalidateOnFocus: false,
      ...options.swr,
    },
  );
  const history = data ?? [];
  const chartHistory = history.map((point) => ({
    timestamp: point.t * 1000,
    value: point.p,
  }));

  return {
    data: chartHistory,
    rawData: data ?? null,
    history,
    chartHistory,
    interval,
    fidelity,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

function mergePolyPnlResponses(responses: PolyPnlResponse[]): PolyPnlResponse {
  const pnlByTime = new Map<number, number>();

  for (const response of responses) {
    for (const point of response) {
      const t = Number(point.t);
      const p = Number(point.p);
      if (!Number.isFinite(t) || !Number.isFinite(p)) continue;

      pnlByTime.set(t, (pnlByTime.get(t) ?? 0) + p);
    }
  }

  return Array.from(pnlByTime.entries())
    .sort(([a], [b]) => a - b)
    .map(([t, p]) => ({ t, p }));
}

function normalizeWallets(wallets: string[]) {
  return [...new Set(wallets.map((wallet) => wallet.trim().toLowerCase()).filter(Boolean))];
}

export default usePolyPnl;
